<template>
    <div
        tabindex="0"
        ref="component"
        class="form-dropdown"
        @focus="show"
        @blur="searchable ? false : hide"
        @keyup.esc="hide"
        @keydown.down.prevent="nextOption"
        @keydown.up.prevent="prevOption"
    >
        <div @click.prevent="show" class="form-control form-control-dropdown" :class="{ 'form-dropdown-open': isOpen, 'form-dropdown-disabled': disabled }" :disabled="disabled">
            <template v-if="internalValue">
                <template v-if="multiple">
                    <span class="dropdown-value multiple-value" v-for="val in internalValue">
                        {{ selectedLabel(val) }}
                        <button class="form-dropdown-remove-selection" @click.stop="select(val)">
                            <i class="fal fa-fw fa-times"></i>
                        </button>
                    </span>
                </template>
                <template v-else>
                    <span class="dropdown-value">{{ selectedLabel(internalValue) }}</span>
                </template>
            </template>
            <template v-else-if="placeholder">
                <span class="dropdown-placeholder">{{ placeholder }}</span>
            </template>
            <i class="fal fa-chevron-down form-dropdown-arrow"></i>
        </div>
        <div class="dropdown-select dropdown-menu show shadow-2xl" ref="list" v-show="isOpen">
            <input tabindex="-1" placeholder="Search" type="text" class="dropdown-search" ref="search" v-model="search" v-if="searchable" />
            <div tabindex="-1" class="form-dropdown-item" @click="select(null)" @keypress.enter.stop.self="select(null)" v-if="allowEmpty && !multiple">
                &nbsp;
            </div>
            <div
                tabindex="-1"
                class="form-dropdown-item"
                v-for="(optionLabel, optionValue) in filteredOptions"
                :class="{ active: isSelected(optionValue) }"
                :key="'dropdownItem' + optionValue"
                @keypress.enter.stop.self="select(optionValue)"
                @click="select(optionValue)"
            >
                {{ optionLabel }}
            </div>
        </div>
    </div>
</template>

<script>
import xor from 'lodash/xor';
import get from 'lodash/get';
import map from 'lodash/map';
import reduce from 'lodash/reduce';
import isObject from 'lodash/isObject';
import flatten from 'lodash/flatten';
import includes from 'lodash/includes';
import toLower from 'lodash/toLower';
import toString from 'lodash/toString';
import pickBy from 'lodash/pickBy';
import isArray from 'lodash/isArray';

export default {
    props: {
        /**
         * Presets the selected options value.
         * @type {Object||Array||String||Number}
         */
        value: {
            type: [Number, String, Array, Object],
        },
        /**
         * Array of available options:
         * @type {Array,Object}
         */
        options: {
            type: [Array, Object],
            required: true,
        },
        /**
         * Equivalent to the `placeholder` attribute on a `<select>` input.
         * @default 'Select option'
         * @type {String}
         */
        placeholder: {
            type: String,
            default: null,
        },
        /**
         * Close menu on select
         * @default true
         * @type {Boolean}
         */
        closeOnSelect: {
            type: Boolean,
            default: true,
        },
        /**
         * Show search input
         * @default true
         * @type {Boolean}
         */
        searchable: {
            type: Boolean,
            default: true,
        },
        /**
         * Equivalent to the `multiple` attribute on a `<select>` input.
         * @default false
         * @type {Boolean}
         */
        multiple: {
            type: Boolean,
            default: false,
        },
        /**
         * Allow to remove all selected values
         * @default true
         * @type {Boolean}
         */
        allowEmpty: {
            type: Boolean,
            default: true,
        },
        /**
         * Key to compare objects
         * @default 'id'
         * @type {String}
         */
        optionValue: {
            type: String,
            default: 'id',
        },
        /**
         * Label to look for in option Object
         * @default 'label'
         * @type {String}
         */
        optionLabel: {
            type: String,
            default: 'name',
        },
        /**
         * Name of the property containing
         * the group label
         * @default null
         * @type {String}
         */
        groupLabel: {
            type: String,
            default: null,
        },
        /**
         * Name of the property containing
         * the group options
         * @default null
         * @type {String}
         */
        groupOptions: {
            type: String,
            default: null,
        },
        /**
         * Enable/Disable input
         * @default false
         * @type {Boolean}
         */
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            preparedOptions: {},
            isGrouped: false,
            isOpen: false,
            search: null,
            internalValue: null,
            popperOptions: {
                placement: 'bottom-start',
                computeStyle: {
                    gpuAcceleration: false,
                },
            },
        };
    },
    created() {
        this.isGrouped = this.groupLabel && this.groupOptions;
        this.preparedOptions = this.prepareOptions(this.options);
        document.addEventListener('click', this.handleDocumentClick);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleDocumentClick);
    },
    computed: {
        filteredOptions() {
            const self = this;

            if (this.search) {
                return pickBy(this.preparedOptions, function (option) {
                    return includes(toLower(option), toLower(self.search));
                });
            }

            return this.preparedOptions;
        },
    },
    methods: {
        show() {
            this.isOpen = true;
            this.$nextTick(() => {
                if (this.searchable) {
                    this.$refs.search.focus();
                }
            });
        },
        hide() {
            if (this.searchable) {
                this.search = null;
            }

            this.isOpen = false;
        },
        select(selection) {
            let value = this.internalValue ? this.internalValue : this.multiple ? [] : null;
            if (this.multiple) {
                value = xor(value, [selection]);
            } else {
                value = selection;
            }
            this.internalValue = value;
            this.$emit('change', value);
            this.$emit('input', value);
            if (this.closeOnSelect) {
                this.hide();
            }
        },
        handleDocumentClick(e) {
            if (!this.$el || this.elementContains(this.$el, e.target)) {
                return;
            }
            this.$emit('documentClick', this);
            this.isOpen = false;
        },
        elementContains(elm, otherElm) {
            if (typeof elm.contains === 'function') {
                return elm.contains(otherElm);
            }
            return false;
        },
        selectedLabel(value) {
            return this.preparedOptions[value] ?? null;
        },
        isSelected(value) {
            return includes(this.internalValue, value);
        },
        prepareOptions(options) {
            const self = this;
            if (this.isGrouped) {
                options = flatten(
                    map(options, (group) => {
                        return get(group, this.groupOptions);
                    }),
                );
            }

            // If is [val, val val]
            // transform to {val: val, val: val}
            if (isArray(options) && !isObject(options[0])) {
                options = reduce(
                    options,
                    (newOptions, val) => {
                        newOptions[val] = val;
                        return newOptions;
                    },
                    {},
                );
            }

            // If is [val, val val]
            // transform to {val: val, val: val}
            if (isArray(options) && !isObject(options[0])) {
                options = reduce(
                    options,
                    (newOptions, val) => {
                        newOptions[val] = val;
                        return newOptions;
                    },
                    {},
                );
            }

            // If is [{ id: 1, name: 'foo' },{ id: 2, name: 'bar' }]
            // Transform to {id: name, id: name}
            if (isArray(options) && isObject(options[0])) {
                options = reduce(
                    options,
                    (newOptions, val) => {
                        newOptions[get(val, self.optionValue)] = get(val, self.optionLabel);
                        return newOptions;
                    },
                    {},
                );
            }

            return this.normalize(options);
        },
        normalize(values) {
            if (isObject(values) && !isArray(values)) {
                return reduce(
                    values,
                    (newOptions, option, key) => {
                        newOptions[toString(key)] = option;
                        return newOptions;
                    },
                    {},
                );
            } else if (isArray(values)) {
                return map(values, (option) => {
                    return toString(option);
                });
            }

            return toString(values);
        },
        nextOption(e) {
            let list = this.$refs.list;
            let main = this.$refs.component;
            let first = list.firstElementChild;
            let last = list.lastElementChild;
            if (document.activeElement === main || document.activeElement === last) {
                first.focus();
            } else {
                document.activeElement.nextElementSibling.focus();
            }
        },
        prevOption(e) {
            let list = this.$refs.list;
            if (document.activeElement === this.$refs.component || document.activeElement === list.firstElementChild) {
                list.lastElementChild.focus();
            } else {
                document.activeElement.previousElementSibling.focus();
            }
        },
    },
    watch: {
        value: {
            deep: true,
            immediate: true,
            handler(newVal, oldVal) {
                this.internalValue = newVal ? this.normalize(newVal) : this.multiple ? [] : null;
            },
        },
    },
};
</script>

<style>
.form-dropdown-disabled,
.form-control-dropdown:disabled,
.form-control-dropdown[readonly],
fieldset[readonly] .form-control-dropdown,
fieldset[disabled] .form-control-dropdown {
    pointer-events: none;
    background-color: #e9ecef;
    color: #a6a6a6;
}

.form-dropdown {
    position: relative;
}

.form-control-dropdown {
    height: auto !important;
    min-height: 50px !important;
    padding-right: 2.2em;
    line-height: 2em;
    cursor: pointer;
}

.dropdown-select {
    width: auto;
    display: block;
    overflow: auto;
    min-width: 100%;
    max-height: 300px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;

    z-index: 1000;
    padding: 0.5rem 0;
    margin: 0;
    font-size: 1rem;
    color: #212529;
    text-align: left;
    list-style: none;
    background-color: #fff;
    background-clip: padding-box;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 0.25rem;
}

.dropdown-search {
    width: 100%;
    border: 0;
    padding: 0.25rem 1.5rem;
}

.form-dropdown-arrow {
    position: absolute;
    top: 0;
    bottom: 0;
    right: 1em;
    display: flex;
    align-content: center;
    align-items: center;
    font-size: 0.8em;
    line-height: 0.8em;
    transition: transform 0.2s ease;
}

.dropdown-placeholder {
    color: #adb5bd;
}

.form-dropdown-open .form-dropdown-arrow {
    transform: rotate(180deg);
}

.multiple-value {
    padding: 2px 5px;
    margin: 0 2px;
    background: #35647e;
    border-radius: 5px;
    color: #fff;
    white-space: nowrap;
}

.multiple-value .form-dropdown-remove-selection {
    border: 0;
    background: none;
    padding: 0;
    color: #fff;
    line-height: 1em;
}

.form-dropdown-item {
    display: block;
    width: 100%;
    padding: 0.25rem 1.5rem;
    clear: both;
    font-weight: 400;
    color: #212529;
    text-align: inherit;
    white-space: nowrap;
    background-color: transparent;
    border: 0;
}

.form-dropdown-item:focus,
.form-dropdown-item:hover {
    color: #16181b;
    text-decoration: none;
    background-color: #f8f9fa;
}

.form-dropdown-item.active,
.form-dropdown-item:active {
    color: #fff;
    text-decoration: none;
    background-color: #35647e;
}

.form-dropdown-item,
.dropdown-value {
    cursor: pointer;
}
</style>
